/**
 * Pop-up configuration model.
 *
 * Like the labelling model, this module deliberately depends on nothing else in
 * the app — `types.ts` imports FROM here so `Layer.popup` can be typed without
 * a cycle.
 *
 * The model is a LIST of content blocks even though v1 edits only the fields
 * block. Esri-style pop-ups mix a field table with text, images and charts in a
 * chosen order; keeping the list shape now means adding those later is additive
 * and never migrates what is already saved.
 *
 * ABSENT MEANS DEFAULT. A layer with no `popup` shows every attribute, exactly
 * as the app did before this existed — so every previously saved module keeps
 * behaving the same way.
 */

export const POPUP_VERSION = 1;

/**
 * How one field's value is written out.
 *
 * Every property is optional and "absent" always means "leave the value as the
 * shared formatter produced it", so a field that needs no formatting carries no
 * formatting configuration.
 */
export interface PopupFieldFormat {
  /** Fixed decimal places for numbers. Absent = as-is. */
  decimals?: number;
  /** Group thousands (1,234,567). Numbers only. */
  thousands?: boolean;
  /** Render the value as a date. Absent = leave numbers/strings alone. */
  dateFormat?: 'date' | 'datetime';
  /** Written before the value, e.g. "₹". */
  prefix?: string;
  /** Written after the value, e.g. " mm". */
  suffix?: string;
}

export interface PopupFieldConfig {
  /** Raw service field name — never the display label. */
  field: string;
  /** Overrides the service alias and our humanised name. Absent = automatic. */
  label?: string;
  visible: boolean;
  format?: PopupFieldFormat;
}

/** The attribute table. v1's only block kind. */
export interface PopupFieldsBlock {
  id: string;
  kind: 'fields';
  /** Order here is the order shown in the pop-up. */
  fields: PopupFieldConfig[];
  /**
   * What happens to a field the configuration has never seen — a column added
   * to the service after this pop-up was set up. False (the default) keeps the
   * pop-up to exactly the chosen fields; true lets new columns through.
   */
  includeUnlisted: boolean;
  /**
   * Whether a chosen field is still listed when the feature has no value for
   * it. Off by default, matching the automatic pop-up — but an asset form
   * often WANTS the blank row, because "Commissioned On: –" is information.
   */
  showEmpty: boolean;
}

export type PopupBlock = PopupFieldsBlock;

export interface LayerPopup {
  version: typeof POPUP_VERSION;
  /** False suppresses the pop-up for this layer entirely. */
  enabled: boolean;
  /** Template with `{field}` tokens. Empty = the layer's name. */
  title: string;
  blocks: PopupBlock[];
}

/* ── Keys ──────────────────────────────────────────────────────────────────
 * A pop-up configuration has to be found again from inside the map, where all
 * that is known about a hit is which service layer produced it. Two namespaces
 * keep a WMS layer called "3" apart from ArcGIS layer id 3.
 */

export function wmsPopupKey(layerName: string): string {
  return `wms:${layerName}`;
}

export function arcgisPopupKey(layerId: number): string {
  return `arcgis:${layerId}`;
}

/**
 * A file-backed layer, keyed by its own id.
 *
 * Not by name, unlike WMS: two shapefiles imported from different folders can
 * carry the same layer name, and the map has no service to disambiguate them.
 * The layer id is the only thing that is unique.
 */
export function localPopupKey(layerId: string): string {
  return `local:${layerId}`;
}

/** A fresh configuration listing the given fields, all visible. */
export function createPopup(fieldNames: string[] = []): LayerPopup {
  return {
    version: POPUP_VERSION,
    enabled: true,
    title: '',
    blocks: [
      {
        id: 'fields',
        kind: 'fields',
        fields: fieldNames.map((field) => ({ field, visible: true })),
        includeUnlisted: false,
        showEmpty: false,
      },
    ],
  };
}

/**
 * Fills in anything a stored configuration is missing.
 *
 * Written defensively because this data round-trips through localStorage and
 * may have been written by an older build.
 */
export function normalisePopup(value: LayerPopup | null | undefined): LayerPopup {
  if (!value || typeof value !== 'object') return createPopup();

  const blocks = Array.isArray(value.blocks) ? value.blocks : [];
  const fieldsBlock = blocks.find((block) => block?.kind === 'fields');

  return {
    version: POPUP_VERSION,
    enabled: value.enabled !== false,
    title: typeof value.title === 'string' ? value.title : '',
    blocks: [
      {
        id: fieldsBlock?.id ?? 'fields',
        kind: 'fields',
        fields: Array.isArray(fieldsBlock?.fields)
          ? fieldsBlock.fields
              .filter((entry): entry is PopupFieldConfig => typeof entry?.field === 'string')
              .map((entry) => ({
                field: entry.field,
                label: typeof entry.label === 'string' ? entry.label : undefined,
                visible: entry.visible !== false,
                format: entry.format ?? undefined,
              }))
          : [],
        includeUnlisted: fieldsBlock?.includeUnlisted === true,
        showEmpty: fieldsBlock?.showEmpty === true,
      },
    ],
  };
}

/** The fields block, which v1 always has exactly one of. */
export function popupFieldsBlock(popup: LayerPopup): PopupFieldsBlock {
  const block = popup.blocks.find((entry): entry is PopupFieldsBlock => entry.kind === 'fields');
  return (
    block ?? { id: 'fields', kind: 'fields', fields: [], includeUnlisted: false, showEmpty: false }
  );
}

/** True when an admin has actually set this pop-up up, rather than left it automatic. */
export function popupIsConfigured(popup: LayerPopup | undefined): boolean {
  if (!popup) return false;
  if (!popup.enabled) return true;
  if (popup.title.trim() !== '') return true;
  return popupFieldsBlock(popup).fields.length > 0;
}

/**
 * Merges the fields a service reports with what the configuration already
 * holds. Configured fields keep their order and settings; newly discovered ones
 * are appended. A configured field the service no longer reports is KEPT — a
 * service that fails to describe itself for one request must not silently wipe
 * a carefully ordered pop-up.
 */
export function mergePopupFields(
  block: PopupFieldsBlock,
  discovered: string[],
): PopupFieldConfig[] {
  const known = new Set(block.fields.map((entry) => entry.field.toLowerCase()));
  const additions = discovered
    .filter((name) => !known.has(name.toLowerCase()))
    .map((field) => ({ field, visible: block.includeUnlisted || block.fields.length === 0 }));
  return [...block.fields, ...additions];
}
